import { Injectable } from '@angular/core';
import { ApiUtilsService } from 'src/app/api/services/api-utils.service';
import { PresentationsService } from 'src/app/presentations/services/presentations.service';
import { cloneDeep } from 'lodash';
import { CompanyApiService } from 'src/app/api/services/company-api.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';

@Injectable({
  providedIn: 'root',
})
export class AlertsService {
  isLoading = false;
  errorSaving = false;
  missingTemplate = false;
  errorMessage = '';
  apiError = '';
  alertKey: any;
  alertSettings: any;
  changeDate: any;
  changedBy: any;
  private _company: any;

  readonly textFieldsValues = [
    { key: 'headline', label: 'Headline' },
    { key: 'description', label: 'Description' },
    { key: 'instruction', label: 'Instruction' },
  ];

  readonly allowedStatusesValues = [
    { key: 'Actual', label: 'Actual' },
    { key: 'Exercise', label: 'Exercise' },
    { key: 'System', label: 'System' },
    { key: 'Test', label: 'Test' },
    { key: 'Draft', label: 'Draft' },
  ];

  readonly allowedCategoriesValues = [
    { key: 'Geo', label: 'Geophysical' },
    { key: 'Met', label: 'Meteorological' },
    { key: 'Safety', label: 'Safety' },
    { key: 'Security', label: 'Security' },
    { key: 'Rescue', label: 'Rescue' },
    { key: 'Fire', label: 'Fire' },
    { key: 'Health', label: 'Health' },
    { key: 'Env', label: 'Environment' },
    { key: 'Transport', label: 'Transport' },
    { key: 'Infra', label: 'Infrastructure' },
    {
      key: 'CBRNE',
      label: 'Chemical, Biological, Radiological, Nuclear and Explosives',
    },
    { key: 'Other', label: 'Other' },
  ];

  readonly allowedUrgenciesValues = [
    { key: 'Immediate', label: 'Immediate' },
    { key: 'Expected', label: 'Expected' },
    { key: 'Future', label: 'Future' },
    { key: 'Past', label: 'Past' },
    { key: 'Unknown', label: 'Unknown' },
  ];

  readonly allowedSeveritiesValues = [
    { key: 'Extreme', label: 'Extreme' },
    { key: 'Severe', label: 'Severe' },
    { key: 'Moderate', label: 'Moderate' },
    { key: 'Minor', label: 'Minor' },
    { key: 'Unknown', label: 'Unknown' },
  ];

  readonly allowedCertaintiesValues = [
    { key: 'Observed', label: 'Observed' },
    { key: 'Likely', label: 'Likely' },
    { key: 'Possible', label: 'Possible' },
    { key: 'Unlikely', label: 'Unlikely' },
    { key: 'Unknown', label: 'Unknown' },
  ];

  constructor(
    private apiUtilsService: ApiUtilsService,
    private companyApiService: CompanyApiService,
    private presentationsService: PresentationsService,
    private companyStateService: CompanyStateService,
  ) {}

  private _clearMessages() {
    this.errorMessage = '';
    this.apiError = '';
  }

  private _updateSettings(company) {
    this._company = company;
    this.alertKey = company.alertKey;
    this.alertSettings = company.alertSettings;
    this.changeDate = company.changeDate;
    this.changedBy = company.changedBy;

    if (
      this.alertSettings &&
      this.alertSettings.presentationId &&
      !this.alertSettings.templateProductCode &&
      !this.alertSettings.presentationName
    ) {
      this.presentationsService
        .getPresentationCached(this.alertSettings.presentationId)
        .then((presentation) => {
          this.alertSettings.presentationName = presentation.name;
        });
    }
  }

  loadSettings() {
    this._updateSettings(
      cloneDeep(this.companyStateService.getCopyOfSelectedCompany(true)),
    );
  }

  exportAlerts(email: string) {
    const companyId = this.companyStateService.getSelectedCompanyId();

    return this.companyApiService
      .exportAlerts(companyId, email)
      .catch((err) => {
        if (!err || !err.message) {
          throw Error('Could not export alerts');
        } else {
          throw err;
        }
      });
  }

  async changePresentation() {
    try {
      const presentationDetails =
        await this.presentationsService.openPresentationSelector();

      this.alertSettings.presentationId = presentationDetails[0];
      this.alertSettings.presentationName = presentationDetails[1];

      return true;
    } catch (error) {
      return false;
    }
  }

  save() {
    this._clearMessages();
    this.isLoading = true;
    this.errorSaving = false;
    if (this.alertSettings.templateProductCode) {
      this.alertSettings.presentationId = '';
      this.alertSettings.presentationName = '';
    } else if (!this.alertSettings.presentationId) {
      this.missingTemplate = true;
      this.isLoading = false;
      return Promise.resolve();
    }
    const presentationName = this.alertSettings.presentationName || '';
    return this.companyApiService
      .updateAlerts(this._company.id, this._company)
      .then(
        (result) => {
          this.companyStateService.updateCompanySettings(result.item);
          this._updateSettings(result.item);
          this.alertSettings.presentationName = presentationName;
        },
        (error) => {
          this.errorSaving = true;
          this.errorMessage = 'Failed to update Alerts.';
          this.apiError = this.apiUtilsService.humanReadableError(
            (error && error.result) || error,
          );
          console.error(this.errorMessage, error);
        },
      )
      .finally(() => {
        this.isLoading = false;
      });
  }

  regenerateAlertKey() {
    this._clearMessages();
    this.isLoading = true;

    this.companyApiService
      .regenerateField(this._company.id, 'alertKey')
      .then(
        (result) => {
          this.alertKey = result.item;
        },
        (error) => {
          this.errorMessage = 'Failed to reset Web Service URL.';
          this.apiError = this.apiUtilsService.humanReadableError(error);
          console.error(this.errorMessage, error);
        },
      )
      .finally(() => {
        this.isLoading = false;
      });
  }
}
